import {
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'
import { ChevronDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router'
import { useLocalStorage } from 'usehooks-ts'

import SyncFiltersWithURL from 'components/admin/SyncFiltersWithUrl'
import BrokerContext from 'components/context/BrokerContext'
import TasksContext from 'components/context/TasksContext'
import {
  getTasksQueryKey,
  type TasksFilters,
} from 'components/hooks/tasks/useGetTasks'
import RowSelectionCountDisplay from 'components/reusable/table/RowSelectionCountDisplay'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import brokersTasks from 'rails_routes/Brokers/TasksPaths'
import { Breadcrumbs, Button } from 'slab'
import { type BrokersTask } from 'utils/web_api.schemas'

import { type Broker } from '../types'
import BulkUpdateTasksDialog from './BulkUpdateTasksDialog'
import TaskDetailsPage from './TaskDetailsPage'
import TasksFiltersView, {
  filterConfigurations,
  type TaskFilterGroupType,
} from './TasksFiltersView'
import TasksQuickFilters from './TasksQuickFilters'
import TasksTable from './TasksTable'

// Column visibility mapping for tasks
const columnVisibilityMapping = {
  created_at: 'Date',
  member_name: 'Member',
  carrier_name: 'Carrier',
  assignees: 'Assignees',
  status: 'Status',
  execution_method: 'Execution Method',
  task_type: 'Task Type',
  job_type: 'Job Type',
  job_handler: 'Handler',
  message: 'Message',
}

const defaultColumnVisibility = {
  created_at: true,
  member_name: true,
  carrier_name: true,
  assignees: true,
  status: true,
  execution_method: true,
  task_type: true,
  job_type: false,
  job_handler: false,
  message: true,
}

const MAX_NUMBER_OF_SELECTED_ITEMS = 100

type TasksViewProps = {
  currentBroker?: Broker
  stripePublishableKey: string
  isUserThatchWorkflowAdmin: boolean
}

// Breadcrumbs component for tasks
const TasksBreadcrumbs = () => {
  const location = useLocation()
  const navigate = useNavigate()

  // Extract task ID from path if present
  const pathSegments = location.pathname.split('/')
  const taskId = pathSegments[3] // /brokers/tasks/:id

  const isTaskDetails = taskId && pathSegments.length > 3

  return (
    <div className='max-w-[2400px] mx-auto px-4 pb-2'>
      <Breadcrumbs>
        <Breadcrumbs.Item>Brokers</Breadcrumbs.Item>
        {isTaskDetails
          ? (
            <>
              <Breadcrumbs.Item onClick={() => navigate(brokersTasks.index())}>
                Tasks
              </Breadcrumbs.Item>
              <Breadcrumbs.Item current>
                Task {taskId.slice(-6)}
              </Breadcrumbs.Item>
            </>
            )
          : (
            <Breadcrumbs.Item current>Tasks</Breadcrumbs.Item>
            )}
      </Breadcrumbs>
    </div>
  )
}

const TasksListView = ({ currentBroker }: { currentBroker?: Broker }) => {
  const [filters, setFilters] = useState<TasksFilters>({})
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 20,
  })
  // No default sorting for the tasks table.
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // Persist column visibility in localStorage
  const [columnVisibilitySettings, setColumnVisibilitySettings] =
    useLocalStorage<VisibilityState>(
      'tasks-column-visibility-settings',
      defaultColumnVisibility
    )

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    columnVisibilitySettings
  )

  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false)
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false)

  const numberOfSelectedItems =
    Object.values(rowSelection).filter(Boolean).length

  // Sync column visibility changes to localStorage
  useEffect(() => {
    setColumnVisibilitySettings(columnVisibility)
  }, [columnVisibility, setColumnVisibilitySettings])

  const handlePageSizeChange = (value: string) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(value, 10),
      pageIndex: 1, // Reset to first page when changing page size
    }))
  }

  return (
    <TasksContext.Provider
      value={{
        getTasksQueryKey: getTasksQueryKey({ filters, pagination, sorting }),
      }}
    >
      <BrokerContext.Provider value={{ currentBroker }}>
        <div className='max-w-[2400px] mx-auto px-4 pb-6'>
          <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2400px] mx-auto'>
            <BulkUpdateTasksDialog
              isOpen={isBulkUpdateOpen}
              setIsOpen={setIsBulkUpdateOpen}
              selectedItems={Object.entries(rowSelection).reduce<string[]>(
                (acc, [key, value]) => (value ? [...acc, key] : acc),
                []
              )}
            />

            <div className='flex items-center justify-between w-full mb-4 gap-x-2'>
              <div className='text-2xl font-semibold w-96'>Tasks</div>
              <div className='flex items-center justify-end w-full gap-x-2'>
                <Button
                  variant='tertiary-destructive'
                  onClick={() => setFilters({})}
                >
                  Clear Filters
                </Button>

                <DropdownMenu
                  open={isActionsMenuOpen}
                  onOpenChange={setIsActionsMenuOpen}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      type='button'
                      variant='secondary'
                      data-testid='actions-menu'
                    >
                      <div className='flex items-center'>
                        Actions
                        <ChevronDown className='ml-1' size={12} />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>Columns</DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          {Object.entries(columnVisibilityMapping).map(
                            ([key, label]) => {
                              return (
                                <DropdownMenuCheckboxItem
                                  key={key}
                                  checked={
                                columnVisibility[
                                  key as keyof typeof columnVisibility
                                ]
                              }
                                  onCheckedChange={(checked) => {
                                    setColumnVisibility((prev) => ({
                                      ...prev,
                                      [key]: checked,
                                    }))
                                  }}
                                >
                                  {label}
                                </DropdownMenuCheckboxItem>
                              )
                            }
                          )}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  type='button'
                  variant='primary'
                  disabled={numberOfSelectedItems === 0}
                  onClick={() => setIsBulkUpdateOpen(true)}
                >
                  Update Selected
                </Button>
              </div>
            </div>

            <TasksQuickFilters filters={filters} setFilters={setFilters} />
            <TasksFiltersView
              filters={filters}
              setFilters={setFilters}
              pagination={pagination}
              setPagination={setPagination}
            />
            <SyncFiltersWithURL<TasksFilters, BrokersTask, TaskFilterGroupType>
              filters={filters}
              setFilters={setFilters}
              pagination={pagination}
              sortableKeys={['created_at', 'status']}
              filterConfigurations={filterConfigurations}
            />

            <div className='h-10 mb-2 flex items-center justify-between w-full'>
              <RowSelectionCountDisplay
                numberOfSelectedItems={numberOfSelectedItems}
                setRowSelection={setRowSelection}
                maxNumberOfSelectedItems={MAX_NUMBER_OF_SELECTED_ITEMS}
              />
              <div className='max-w-[50%] flex items-center justify-between space-x-2'>
                <div className='font-light text-sm text-nowrap'>
                  Records per page
                </div>
                <div className='w-[100px]'>
                  <Select
                    value={pagination.pageSize.toString()}
                    onValueChange={handlePageSizeChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Page size' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='10'>10</SelectItem>
                      <SelectItem value='20'>20</SelectItem>
                      <SelectItem value='50'>50</SelectItem>
                      <SelectItem value='100'>100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <TasksTable
              filters={filters}
              setFilters={setFilters}
              pagination={pagination}
              setPagination={setPagination}
              sorting={sorting}
              setSorting={setSorting}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              columnVisibilityOverrides={columnVisibility}
            />
          </div>
        </div>
      </BrokerContext.Provider>
    </TasksContext.Provider>
  )
}

const TasksView = (props: TasksViewProps) => {
  return (
    <BrowserRouter>
      <div className='min-h-screen'>
        <TasksBreadcrumbs />
        <Routes>
          {/* Main tasks list view */}
          <Route
            path={brokersTasks.index()}
            element={<TasksListView currentBroker={props.currentBroker} />}
          />
          {/* Individual task details */}
          <Route
            path='/brokers/tasks/:id'
            element={
              <TaskDetailsPage
                isUserThatchWorkflowAdmin={props.isUserThatchWorkflowAdmin}
                stripePublishableKey={props.stripePublishableKey}
              />
            }
          />
        </Routes>
      </div>
    </BrowserRouter>
  )
}

export default TasksView
